import { invalidateRequestTags } from './requestCache';
import { eventTags } from './requestPolicy';

export const LIVE_EVENT = 'teamDataInvalidated';
const subscriptions = new Set();
let timer;
let eventTimer;
let connected = false;
let pendingTags = new Set();

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
const intersects = (a, b) => a.some((tag) => b.includes(tag));
const intervalFor = (entry) => entry.interval * (connected ? 2 : 1) * Math.min(8, 2 ** entry.failures);

const run = async (entry) => {
  if (!visible() || entry.running || !subscriptions.has(entry)) return;
  entry.running = true;
  entry.dirty = false;
  try {
    await entry.callback();
    entry.failures = 0;
  } catch (_) {
    entry.failures += 1;
  } finally {
    entry.running = false;
    entry.next = Date.now() + intervalFor(entry);
    // A change during a read must get a fresh read after it completes.
    if (entry.dirty && visible() && subscriptions.has(entry)) entry.next = Date.now() + 250;
    schedule();
  }
};

const tick = () => {
  timer = undefined;
  if (!visible()) return;
  const now = Date.now();
  subscriptions.forEach((entry) => {
    if (!entry.running && entry.next <= now) run(entry);
  });
  schedule();
};

function schedule() {
  clearTimeout(timer);
  timer = undefined;
  if (!visible()) return;
  const next = Math.min(...Array.from(subscriptions).filter((entry) => !entry.running).map((entry) => entry.next));
  if (Number.isFinite(next)) timer = setTimeout(tick, Math.max(0, next - Date.now()));
}

const handleEvent = (event) => {
  const detail = event.detail || {};
  const tags = detail.tags || eventTags(detail.event || detail.source || '');
  if (!detail.cacheInvalidated) invalidateRequestTags(tags);
  subscriptions.forEach((entry) => {
    if (intersects(entry.resources, tags)) {
      entry.dirty = true;
      entry.next = Date.now() + 250;
    }
  });
  schedule();
};

const handleVisibility = () => {
  if (visible()) {
    subscriptions.forEach((entry) => { entry.dirty = true; entry.next = Date.now() + 250; });
  }
  schedule();
};

export const setLiveConnection = (value) => {
  const reconnected = value && !connected;
  connected = value;
  if (reconnected) handleVisibility();
};

// Collapse a burst of desktop events into one notification for mounted pages.
export const publishLiveEvent = (detail = {}) => {
  eventTags(detail.event || detail.source || '').forEach((tag) => pendingTags.add(tag));
  if (eventTimer) return;
  eventTimer = setTimeout(() => {
    eventTimer = undefined;
    const tags = Array.from(pendingTags);
    pendingTags = new Set();
    invalidateRequestTags(tags);
    window.dispatchEvent(new CustomEvent(LIVE_EVENT, { detail: { ...detail, tags, cacheInvalidated: true } }));
  }, 200);
};

export const subscribeLiveRefresh = (callback, { resources, interval = 60_000 }) => {
  const entry = { callback, resources, interval: Math.max(1000, interval), failures: 0, next: Date.now() + interval, running: false, dirty: false };
  if (subscriptions.size === 0) {
    window.addEventListener(LIVE_EVENT, handleEvent);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleVisibility);
  }
  subscriptions.add(entry);
  schedule();
  return () => {
    subscriptions.delete(entry);
    if (subscriptions.size === 0) {
      window.removeEventListener(LIVE_EVENT, handleEvent);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleVisibility);
    }
    schedule();
  };
};
